/**
 * Which parts of the registration wizards apply to a person, by age (spec 2026-09-30,
 * 6.3). The bands are the completeness rule's (`pitaDariUsia`, `BATAS_PITA`), so a person
 * is never asked less than completeness later requires. With a partial birth date the
 * oldest possible age decides (ask rather than skip): '2019' read on 30 September 2026 is
 * 6 or 7 years old and gets the school band. Without a birth date, or with one that does
 * not read or lies after today, the age is unknown and the adult band applies.
 *
 * Tambah umat, Pendaftaran's guided mode and the Periksa pages all read this one function.
 */
import { type KolomKelengkapanUmat, type PitaUsia, pitaDariUsia } from './completeness'
import { parsePartialDate, partialDateEarliest, usiaPada } from './dates/partial'

export const ITEM_LANGKAH = [
  'dataDiri',
  'hubungan',
  'baptis',
  'komuni',
  'krisma',
  'perkawinan',
  'pendidikan',
  'pekerjaan',
  'kontak',
] as const
export type ItemLangkah = (typeof ITEM_LANGKAH)[number]

export interface LangkahUsia {
  readonly pita: PitaUsia
  /** The oldest completed age the birth date allows; null when it is unknown. */
  readonly usia: number | null
  readonly berlaku: Readonly<Record<ItemLangkah, boolean>>
}

/** The completeness columns and the wizard item that asks for each. */
export const ITEM_KOLOM: Readonly<Record<KolomKelengkapanUmat, ItemLangkah>> = {
  nama_lengkap: 'dataDiri',
  jenis_kelamin: 'dataDiri',
  tanggal_lahir: 'dataDiri',
  nik: 'dataDiri',
  baptis: 'baptis',
  pendidikan_kode: 'pendidikan',
  pekerjaan_kode: 'pekerjaan',
  status_perkawinan: 'perkawinan',
}

/** Completed years on `pada` for the earliest day the partial date can mean. */
export function usiaTertua(
  tanggalLahir: string | null | undefined,
  pada: string | Date,
): number | null {
  if (tanggalLahir === null || tanggalLahir === undefined) return null
  const lahir = tanggalLahir.trim()
  if (parsePartialDate(lahir) === null) return null
  const usia = usiaPada(partialDateEarliest(lahir), pada)
  if (usia === null || usia.tahun < 0) return null
  return usia.tahun
}

export function langkahUsia(
  tanggalLahir: string | null | undefined,
  pada: string | Date,
): LangkahUsia {
  const usia = usiaTertua(tanggalLahir, pada)
  const pita = pitaDariUsia(usia)
  const sekolah = pita !== 'anak'
  const dewasa = pita === 'dewasa'
  return {
    pita,
    usia,
    berlaku: {
      dataDiri: true,
      hubungan: true,
      baptis: true,
      komuni: sekolah,
      krisma: sekolah,
      perkawinan: dewasa,
      pendidikan: sekolah,
      pekerjaan: dewasa,
      kontak: sekolah,
    },
  }
}
